import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { FileText, Loader2 } from 'lucide-react';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import docshawkService from '../services/docshawkService';

// Pin the worker to the exact installed pdfjs-dist version (avoids CRA worker bundling issues).
pdfjs.GlobalWorkerOptions.workerSrc =
  `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

interface Props {
  documentId: string;
  mimeType?: string;
  highlight?: string | null;   // value string to highlight
  highlightPage?: number;      // 1-based page to scroll to
}

const DocViewer: React.FC<Props> = ({ documentId, mimeType, highlight, highlightPage }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [width, setWidth] = useState(600);
  const wrapRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const isText = (mimeType || '').includes('text');

  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    (async () => {
      try {
        const blob = await docshawkService.getDocumentFile(documentId);
        if (cancelled) return;
        if (isText) setText(await blob.text());
        else { url = URL.createObjectURL(blob); setBlobUrl(url); }
      } catch {
        if (!cancelled) setError('Could not load document');
      }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [documentId, isText]);

  useEffect(() => {
    const measure = () => setWidth(Math.min(900, (wrapRef.current?.clientWidth || 620) - 24));
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [blobUrl]);

  // Scroll to the highlighted field's page.
  useEffect(() => {
    if (highlightPage && pageRefs.current[highlightPage]) {
      pageRefs.current[highlightPage]!.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [highlightPage, highlight]);

  const hlRe = useMemo(
    () => (highlight && highlight.trim().length > 1 ? new RegExp(escapeRe(highlight.trim()), 'gi') : null),
    [highlight]);

  const textRenderer = useMemo(() => (
    (layer: { str: string }) =>
      hlRe ? layer.str.replace(hlRe, (m) => `<mark class="mx-hl">${m}</mark>`) : layer.str
  ), [hlRe]);

  const renderTextHighlighted = (content: string) => {
    if (!hlRe) return content;
    const parts: React.ReactNode[] = [];
    let last = 0; let m: RegExpExecArray | null; hlRe.lastIndex = 0;
    while ((m = hlRe.exec(content))) {
      parts.push(content.slice(last, m.index));
      parts.push(<mark key={m.index} className="mx-hl">{m[0]}</mark>);
      last = m.index + m[0].length;
    }
    parts.push(content.slice(last));
    return parts;
  };

  if (error) return <div className="mx-card mx-card-pad mx-muted">{error}</div>;

  return (
    <div ref={wrapRef} className="mx-card" style={{ overflow: 'auto', maxHeight: 'calc(100vh - 220px)',
      background: 'var(--mx-surface-2)' }}>
      {isText ? (
        text == null ? <Loading /> : (
          <pre style={{ padding: 20, margin: 0, whiteSpace: 'pre-wrap', fontSize: 13,
            lineHeight: 1.6, fontFamily: 'var(--mx-mono, monospace)' }}>
            {renderTextHighlighted(text)}
          </pre>
        )
      ) : !blobUrl ? <Loading /> : (
        <Document file={blobUrl} loading={<Loading />} onLoadError={() => setError('Failed to render PDF')}
          onLoadSuccess={({ numPages }) => setNumPages(numPages)}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: 14 }}>
            {Array.from({ length: numPages }, (_, i) => i + 1).map((p) => (
              <div key={p} ref={(el) => { pageRefs.current[p] = el; }}
                style={{ boxShadow: 'var(--mx-shadow-md)' }}>
                <Page pageNumber={p} width={width} customTextRenderer={textRenderer as any}
                  renderAnnotationLayer={false} />
              </div>
            ))}
          </div>
        </Document>
      )}
    </div>
  );
};

const Loading = () => (
  <div style={{ display: 'grid', placeItems: 'center', padding: 60, color: 'var(--mx-text-muted)' }}>
    <FileText size={26} /><span style={{ marginTop: 8, fontSize: 13, display: 'inline-flex', gap: 6 }}>
      <Loader2 size={14} className="mx-spin" /> loading document…</span>
  </div>
);

export default DocViewer;
